import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getServicePageUI } from '../../Public/service-page-data';
import SystemDataService from '../../Services/SystemDataService';
import ImageComponent from '../../Utils/Image/image-component';
import { ButtonPrimary, ButtonSecondary } from '../../Utils/Buttons/button-component';
import useScrollOrNavigate from '../../Hooks/useScrollOrNavigate';

export default function ServicesMiddleSection({ processData, storiesData }) {
    const scrollOrNavigate = useScrollOrNavigate();
    const systemData = useSelector((s) => s.systemData || {});

    const { dir = 'ltr', language = 'en' } = systemData;

    const uiContent = getServicePageUI(language).middleSection;

    // --- Content Resolvers ---
    const processTitle = processData?.title || uiContent.processDefaultTitle || "Our Precision Process";
    const processContent = processData?.content || uiContent.processDefaultContent || "Precision Engineering. Permitted Reliability.";
    const processSteps = processData?.steps || [];

    const storiesTitle = storiesData?.title || uiContent.storiesDefaultTitle || "Our Guarantee in Action";
    const storiesContent = storiesData?.content || uiContent.storiesDefaultContent || "";
    const storiesImage = storiesData?.image || "https://sawpy-assets.s3.us-east-2.amazonaws.com/tropicalmarine/gallery/1777551515731.jpg";

    // --- Animation Logic ---
    const [activeIndex, setActiveIndex] = useState(0);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const sectionRef = useRef(null);

    // 1. Automatic Loop for Desktop
    useEffect(() => {
        if (!isIntersecting || processSteps.length === 0) return;

        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % processSteps.length);
        }, 3500); // Cycle every 3.5 seconds

        return () => clearInterval(interval);
    }, [isIntersecting, processSteps.length]);

    // 2. Intersection Observer for Entry
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => setIsIntersecting(entry.isIntersecting),
            { threshold: 0.2 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    const renderParagraphs = (text, textColorClass = "text-primary-800") => {
        if (!text) return null;
        return text.split('\n\n').map((para, index) => (
            <p key={index} className={`mb-6 last:mb-0 leading-relaxed text-lg md:text-xl font-medium ${textColorClass}`}>
                {para}
            </p>
        ));
    };

    return (
        <div className="w-full flex flex-col font-assistant" dir={dir}>

            {/* --- SECTION 1: PRECISION ENGINEERING (Elegant Timeline) --- */}
            <section ref={sectionRef} className="w-full bg-white py-16 px-6 relative overflow-hidden">
                <div className="max-w-6xl mx-auto relative z-10">

                    {/* Header */}
                    <div className="flex flex-col items-center text-center mb-16">
                        <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-primary-800 leading-tight uppercase mb-6">
                            {processTitle}
                        </h2>
                        <div className="max-w-3xl opacity-70">
                            {renderParagraphs(processContent)}
                        </div>
                    </div>

                    <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

                        {/* 1. Elegant Vertical Sidebar / Horizontal Pagers */}
                        <div className="flex flex-row lg:flex-col justify-center gap-4 lg:gap-8 w-full lg:w-auto">
                            {processSteps.map((step, index) => {
                                const isActive = activeIndex === index;
                                return (
                                    <button
                                        key={index}
                                        onClick={() => setActiveIndex(index)}
                                        className="relative group flex items-center gap-4 transition-all duration-500"
                                    >
                                        <div className={`w-12 h-12 lg:w-16 lg:h-16 rounded-full flex items-center justify-center transition-all duration-500 ${isActive ? 'bg-primary-800 text-white shadow-xl' : 'bg-primary-50 text-primary-800 opacity-40 hover:opacity-100'}`}>
                                            <span className="text-lg lg:text-2xl font-black">0{index + 1}</span>
                                        </div>
                                        <span className={`hidden lg:block uppercase font-black tracking-widest text-sm transition-all duration-500 ${isActive ? 'text-primary-900 translate-x-2' : 'text-primary-900/30'}`}>
                                            {step.title}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* 2. Main Content Stage (Elegant Transition) */}
                        <div className="flex-1 w-full min-h-[350px] lg:min-h-[400px] bg-primary-50/30 rounded-[3rem] p-10 lg:p-16 flex flex-col justify-center border border-primary-100">
                            <div key={activeIndex} className="animate-fade-in transition-all duration-1000">
                                <h3 className="text-2xl lg:text-4xl font-black text-secondary-500 uppercase mb-6 ">
                                    {processSteps[activeIndex]?.title}
                                </h3>
                                <div className="text-lg md:text-2xl lg:text-3xl text-primary-800 font-medium leading-relaxed italic">
                                    “{processSteps[activeIndex]?.description}”
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* --- SECTION 2: CASE STUDY --- */}
            <section className="relative w-full py-32 px-6 overflow-hidden bg-primary-800 text-white">
                <div className="absolute inset-0 w-full h-full z-0 pointer-events-none opacity-0">
                    <ImageComponent src={storiesImage} alt="Success Background" className="w-full h-full object-cover" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto">
                    <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
                        <div className="w-full lg:w-1/2 flex justify-center">
                            <div className="relative w-full max-w-lg aspect-square">
                                <div className="relative w-full h-full rounded-full border-8 border-white overflow-hidden shadow-2xl z-10">
                                    <ImageComponent src={storiesImage} alt="HVAC Success" className="w-full h-full object-cover" />
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-start">
                            <span className="inline-block py-2 px-8 rounded-full bg-secondary-500 text-white text-sm font-black tracking-[0.25em] uppercase mb-8 shadow-xl">
                                {uiContent.caseStudyLabel}
                            </span>
                            <h2 className="text-5xl md:text-7xl font-black text-white mb-8 er leading-[0.95] uppercase">
                                {storiesTitle}
                            </h2>
                            <div className="max-w-2xl italic mb-12">
                                {renderParagraphs(storiesContent, "text-white/90")}
                            </div>
                            <ButtonSecondary onClick={(e) => scrollOrNavigate(e, 'our-work', '/our-work')} size="lg" className="!px-16 !py-5 shadow-2xl !bg-white !text-primary-900 font-black uppercase tracking-widest hover:!bg-secondary-500 hover:!text-white transition-all">
                                {uiContent.viewprojects || "View All Projects"}
                            </ButtonSecondary>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}